/**
 * TemplatePreviewCard — Phase 1 RXF
 *
 * Shows a radiologist-friendly summary of what sections the report will contain,
 * auto-fetched from /rxf/templates/preview when a CPT exam is selected.
 * No LLM call — <50ms pure-static response.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GradeIcon from '@mui/icons-material/Grade';
import { getTemplatePreview, type TemplatePreviewResponse } from '../../services/rxfService';

interface TemplatePreviewCardProps {
  cptCode?: string;
  modality?: string;
  bodyRegion?: string;
}

const TemplatePreviewCard: React.FC<TemplatePreviewCardProps> = ({
  cptCode,
  modality,
  bodyRegion,
}) => {
  const [preview, setPreview] = useState<TemplatePreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!cptCode && !modality) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getTemplatePreview({ cpt_code: cptCode, modality, body_region: bodyRegion })
      .then((res) => { if (!cancelled) { setPreview(res); setLoading(false); } })
      .catch(() => { if (!cancelled) { setLoading(false); } });
    return () => { cancelled = true; };
  }, [cptCode, modality, bodyRegion]);

  if (!cptCode && !modality) return null;

  return (
    <Card variant="outlined" sx={{ mt: 1.5, borderColor: 'primary.light' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutlineIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" color="primary">
              {loading ? 'Loading template…' : (preview?.exam_name || 'Report Template')}
            </Typography>
            {preview?.has_grading && (
              <Tooltip title="This exam has structured grading systems (e.g. PI-RADS, BI-RADS)">
                <Chip
                  size="small"
                  icon={<GradeIcon fontSize="small" />}
                  label="Grading"
                  color="secondary"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={18} />
            </Box>
          ) : preview ? (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                SECTIONS
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {preview.sections.map((s) => (
                  <Chip
                    key={s.name}
                    label={s.name}
                    size="small"
                    variant={s.is_required ? 'filled' : 'outlined'}
                    color={s.is_required ? 'primary' : 'default'}
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
              </Stack>
            </>
          ) : null}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default TemplatePreviewCard;
